import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Play, Square, ArrowRight, Loader, Newspaper, Mic, BookOpen, Globe } from 'lucide-react';
import NavbarLanding from '../components/NavbarLanding';
import Navigation from '../components/Navigation';
import { ClickableText } from '../components/news/ClickableText';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { PracticeModal } from '../components/PracticeModal';
import { selectBestVoice } from '../hooks/useSpeechSynthesis';
import { API_BASE_URL } from '../config/api';


const SentencePractice = ({ sentence }: { sentence: string }) => {
  const [isPracticing, setIsPracticing] = useState(false);
  const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({});
  const [practiceState, setPracticeState] = useState<any>({
    isRecording: false,
    transcription: '',
    audioBlob: null,
    attempts: 0,
    score: null,
  });

  useEffect(() => {
    setPracticeState((prev: any) => ({
      ...prev,
      transcription: transcript
    }));
  }, [transcript]);

  const handleStartRecording = async () => {
    setPracticeState((prev: any) => ({
      ...prev,
      attempts: prev.attempts + 1,
      score: null,
      transcription: ''
    }));
    await startRecording();
  };

  const handleClosePractice = () => {
    setIsPracticing(false);
    setPracticeState({
      isRecording: false,
      transcription: '',
      audioBlob: null,
      attempts: 0,
      score: null,
    });
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-3 border-b border-amber-100/50 last:border-0">
      <div className="text-amber-900 flex-1">
        <ClickableText text={sentence} />
      </div>
      <button
        onClick={() => setIsPracticing(true)}
        className="flex-shrink-0 px-4 py-2 bg-white text-amber-700 rounded-lg text-sm border border-amber-200 shadow-sm font-medium flex items-center gap-2 hover:bg-amber-50 transition-colors"
      >
        <Mic className="w-4 h-4" /> Practice
      </button>

      {isPracticing && (
        <PracticeModal
          example={{ text: sentence, partOfSpeech: 'phrase' }}
          practiceState={practiceState}
          onClose={handleClosePractice}
          onStartRecording={handleStartRecording}
          onStopRecording={stopRecording}
          isRecording={isRecording}
          activity={false}
        />
      )}
    </div>
  );
};

export default function NewsArticle() {
  const location = useLocation();
  const navigate = useNavigate();
  const { source, rawArticle, topic, preloadedArticle } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [article, setArticle] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const activeSessionIdRef = useRef<number>(0);
  const resumeIntervalRef = useRef<any>(null);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const [ttsState, setTtsState] = useState<{ charIndex: number } | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const updateVoices = () => {
        const v = window.speechSynthesis.getVoices();
        if (v.length > 0) {
          voicesRef.current = v;
        }
      };
      updateVoices();
      window.speechSynthesis.onvoiceschanged = updateVoices;
    }
    return () => {
      if (resumeIntervalRef.current) clearInterval(resumeIntervalRef.current);
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!source) {
      navigate('/news');
      return;
    }

    const fetchNews = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!rawArticle) {
          throw new Error("Missing article data");
        }
        
        // Phase 1: Process Content & Vocab
        const res1 = await fetch(`${API_BASE_URL}/api/news/process-article`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ article: rawArticle })
        });
        const data1 = await res1.json();
        if (!data1.success) throw new Error(data1.message);
        
        const processedArticle = data1.data;
        // Set article so user can start reading!
        setArticle(processedArticle);
        setLoading(false);

        // Phase 2: Background Activities Generation
        setActivitiesLoading(true);
        try {
          const res2 = await fetch(`${API_BASE_URL}/api/news/generate-activities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              articleId: rawArticle.id, 
              rewrittenContent: processedArticle.content 
            })
          });
          const data2 = await res2.json();
          if (data2.success) {
            setArticle((prev: any) => ({
              ...prev,
              multipleChoice: data2.data.multipleChoice,
              fillInTheBlank: data2.data.fillInTheBlank
            }));
          }
        } catch(err) {
          console.error("Failed to generate activities", err);
        } finally {
          setActivitiesLoading(false);
        }
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchNews();
  }, [source, topic, rawArticle, preloadedArticle, navigate]);

  const paragraphs = article?.content ? article.content.split('\n').filter((p: string) => p.trim() !== '') : [];
  const displayTitle = rawArticle?.title || article?.title || '';
  const fullText = article ? displayTitle + ".\n\n" + paragraphs.join('\n\n') : "";

  interface SpeechChunk {
    text: string;
    charIndex: number;
  }

  const stopSpeech = () => {
    activeSessionIdRef.current++;
    if (resumeIntervalRef.current) {
      clearInterval(resumeIntervalRef.current);
      resumeIntervalRef.current = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    currentUtteranceRef.current = null;
    setIsPlaying(false);
    setTtsState(null);
  };

  const startSpeech = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    if (!fullText.trim()) return;

    stopSpeech();

    const sessionId = ++activeSessionIdRef.current;
    setIsPlaying(true);

    try {
      if (typeof window.speechSynthesis.resume === 'function') {
        window.speechSynthesis.resume();
      }
    } catch (e) {
      console.warn('speechSynthesis resume error:', e);
    }

    if (resumeIntervalRef.current) clearInterval(resumeIntervalRef.current);
    resumeIntervalRef.current = setInterval(() => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking) {
        if (typeof window.speechSynthesis.resume === 'function') {
          window.speechSynthesis.resume();
        }
      }
    }, 5000);

    const chunks: SpeechChunk[] = [];
    if (displayTitle.trim()) {
      chunks.push({ text: displayTitle.trim(), charIndex: 0 });
    }

    const titlePrefix = displayTitle + ".\n\n";
    let runningCharOffset = titlePrefix.length;

    paragraphs.forEach((paragraph: string) => {
      const sentenceRegex = /[^.!?]+(?:[.!?]+(?=\s|$)|$)/g;
      let match;
      let addedAny = false;
      while ((match = sentenceRegex.exec(paragraph)) !== null) {
        const trimmed = match[0].trim();
        if (trimmed) {
          chunks.push({
            text: trimmed,
            charIndex: runningCharOffset + match.index
          });
          addedAny = true;
        }
      }
      if (!addedAny && paragraph.trim()) {
        chunks.push({ text: paragraph.trim(), charIndex: runningCharOffset });
      }
      runningCharOffset += paragraph.length + 2;
    });

    if (chunks.length === 0) {
      setIsPlaying(false);
      return;
    }

    let chunkIdx = 0;

    const playChunk = (index: number) => {
      if (activeSessionIdRef.current !== sessionId) return;

      if (index >= chunks.length) {
        stopSpeech();
        return;
      }

      const chunk = chunks[index];
      const utterance = new SpeechSynthesisUtterance(chunk.text);
      currentUtteranceRef.current = utterance;
      utterance.lang = 'en-US';
      utterance.rate = 0.85;

      const voices = voicesRef.current.length > 0 ? voicesRef.current : window.speechSynthesis.getVoices();
      const preferredVoice = selectBestVoice(voices, 'en-US');
      if (preferredVoice) {
        try {
          utterance.voice = preferredVoice;
        } catch (e) {
          console.warn('Error setting voice:', e);
        }
      }

      utterance.onstart = () => {
        if (activeSessionIdRef.current !== sessionId) return;
        setTtsState({ charIndex: chunk.charIndex });
      };

      utterance.onend = () => {
        if (activeSessionIdRef.current !== sessionId) return;
        chunkIdx++;
        playChunk(chunkIdx);
      };

      utterance.onerror = (e) => {
        if (activeSessionIdRef.current !== sessionId) return;
        if (e.error === 'interrupted' || e.error === 'canceled') {
          return;
        }
        console.warn('Utterance error:', e.error);
        chunkIdx++;
        playChunk(chunkIdx);
      };

      try {
        window.speechSynthesis.speak(utterance);
        if (typeof window.speechSynthesis.resume === 'function') {
          window.speechSynthesis.resume();
        }
      } catch (err) {
        console.error('Error in speak:', err);
        chunkIdx++;
        playChunk(chunkIdx);
      }
    };

    playChunk(0);
  };

  const handleSpeak = () => {
    if (isPlaying) {
      stopSpeech();
    } else {
      startSpeech();
    }
  };

  const selectVoaArticle = (item: any) => {
     const cleanContent = item.contentSnippet || item.content.replace(/<[^>]*>?/gm, '');
     setArticle({
       title: item.title,
       content: cleanContent,
       isVoa: true
     });
  };

  const handleContinue = () => {
     navigate('/news/activities', { state: { article } });
  };

  return (
    <>
      <NavbarLanding isLanding={false} />
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <main className="flex-1 pb-16 md:pb-0 md:ml-64 p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                <p className="text-gray-600 font-medium">
                  Loading the news and the activities for you...
                </p>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 p-6 rounded-xl border border-red-200">
                <h3 className="font-bold text-lg mb-2">Oops! Something went wrong.</h3>
                <p>{error}</p>
                <button onClick={() => navigate('/news')} className="mt-4 px-4 py-2 bg-red-100 rounded-lg hover:bg-red-200">
                  Go Back
                </button>
              </div>
            ) : article ? (
              <div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  {rawArticle?.image && (
                    <img
                      src={rawArticle.image}
                      alt={rawArticle?.title || article.title}
                      className="w-full h-56 md:h-72 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div className="p-6 md:p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <Globe className="w-7 h-7 text-indigo-600 flex-shrink-0" />
                    <ClickableText 
                      text={displayTitle} 
                      ttsCharIndex={ttsState?.charIndex} 
                      ttsFullText={fullText} 
                      paragraphStartIndex={0} 
                    />
                  </h2>
                  <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500 font-medium capitalize">
                        {topic}
                      </span>
                    </div>

                    <button
                      onClick={handleSpeak}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        isPlaying 
                          ? 'bg-red-100 text-red-600 hover:bg-red-200 shadow-inner' 
                          : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:scale-105 shadow-sm'
                      }`}
                    >
                      {isPlaying ? <Square className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 ml-1 fill-current" />}
                    </button>
                  </div>

                  {article.vocabulary && article.vocabulary.length > 0 && (
                    <div className="mb-10">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" /> Target Vocabulary
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {article.vocabulary.map((vocab: any, index: number) => (
                          <div key={index} className="bg-gray-50 border border-gray-100 p-4 rounded-xl hover:border-indigo-200 transition-colors">
                             <div className="flex items-start justify-between">
                               <div>
                                 <p className="font-bold text-gray-900 text-lg">
                                   <ClickableText text={vocab.word} />
                                 </p>
                                 <p className="text-xs text-indigo-600 font-medium">{vocab.partOfSpeech}</p>
                               </div>
                             </div>
                             <p className="text-sm text-gray-600 mt-2">{vocab.definition}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {article.targetSentences && (
                    <div className="mb-10 p-6 bg-amber-50 rounded-2xl border border-amber-100">
                      <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Mic className="w-4 h-4" /> Target Sentences
                      </h3>
                      <div className="flex flex-col">
                        {article.targetSentences.map((s: string, idx: number) => (
                          <SentencePractice key={idx} sentence={s} />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="prose prose-lg max-w-none prose-p:leading-relaxed prose-headings:font-bold prose-a:text-indigo-600">
                    {paragraphs.map((paragraph: string, index: number) => {
                      // Calculate the actual character offset of this paragraph within fullText
                      // fullText = title + ".\n\n" + paragraph[0] + "\n\n" + paragraph[1] + ...
                      const titlePrefix = displayTitle + ".\n\n";
                      let charOffset = titlePrefix.length;
                      for (let i = 0; i < index; i++) {
                        charOffset += paragraphs[i].length + 2; // +2 for "\n\n" separator
                      }
                      return (
                        <p key={index} className="mb-6 text-gray-800">
                          <ClickableText text={paragraph} ttsCharIndex={ttsState?.charIndex} ttsFullText={fullText} paragraphStartIndex={charOffset} />
                        </p>
                      );
                    })}
                  </div>

                  <div className="mt-12 pt-8 border-t border-gray-100 flex justify-end">
                    {activitiesLoading ? (
                      <div className="flex items-center gap-3 text-indigo-600 bg-indigo-50 px-6 py-3 rounded-xl">
                        <Loader className="w-5 h-5 animate-spin" />
                        <span className="font-medium">Generating activities...</span>
                      </div>
                    ) : (
                      <button
                        onClick={handleContinue}
                        className="px-8 py-4 bg-indigo-600 text-white rounded-xl font-bold text-lg hover:bg-indigo-700 flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
                      >
                        Activities & Quiz
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                </div>
              </div>
            ) : null}
            
          </div>
        </main>
      </div>
    </>
  );
}
